import { Link, Typography } from '@material-ui/core';
import { FormikTextField } from '@superdispatch/forms';
import { Stack } from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { Countdown } from 'shared/ui/Countdown';
import { hasOnlyDigits } from 'shared/utils/StringUtils';
import { yupObject, yupString } from 'shared/utils/YupUtils';

export type SendVerificationCodeMethod = 'email' | 'sms';

const CODE_LENGTH_ERROR = 'Your code must be composed of 6 digits';

export const verificationCodeSchema = yupObject({
  code: yupString()
    .required(CODE_LENGTH_ERROR)
    .length(6, CODE_LENGTH_ERROR)
    .test('only-digits', CODE_LENGTH_ERROR, hasOnlyDigits),
});

interface VerificationCodeFormProps {
  recipient?: string;
  verificationMethod: SendVerificationCodeMethod;
  resendAvailableAt: DateTime | undefined;
  onResend: () => void;
  onSendEmail: () => void;
  onSendTextMessage: () => void;
  hideSubmitButton?: boolean;
}

export function VerificationCodeForm({
  recipient,
  verificationMethod,
  resendAvailableAt,
  onResend,
  onSendEmail,
  onSendTextMessage,
  hideSubmitButton = false,
}: VerificationCodeFormProps) {
  const { isSubmitting, isValid, dirty } = useFormikContext();
  const isSubmitDisabled = !dirty || !isValid;
  const isSMSVerificationMethod = verificationMethod === 'sms';
  const displayedRecipient = isSMSVerificationMethod
    ? getMaskedPhone(recipient)
    : recipient;

  return (
    <Stack space="medium">
      <Stack space="small">
        <Typography>
          For your protection we&apos;ve sent you a temporary 6-digit code at{' '}
          <b>{displayedRecipient}</b>. Enter this code to continue changing your
          bank account.
        </Typography>

        <FormikTextField
          label="Verification Code"
          name="code"
          fullWidth={true}
          disabled={isSubmitting}
          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
        />

        <ResendHelper
          resendAvailableAt={resendAvailableAt}
          onResend={onResend}
        />
      </Stack>

      <Stack space="small" align="left">
        {!hideSubmitButton && (
          <Box>
            <Button
              type="submit"
              pending={isSubmitting}
              disabled={isSubmitDisabled}
            >
              Continue
            </Button>
          </Box>
        )}

        {resendAvailableAt ? (
          <Countdown targetDate={resendAvailableAt}>
            {(timeLeft, isElapsed) => (
              <ToggleVerificationMethodLink
                disabled={!!timeLeft && !isElapsed}
                verificationMethod={verificationMethod}
                onSendEmail={onSendEmail}
                onSendTextMessage={onSendTextMessage}
              />
            )}
          </Countdown>
        ) : (
          <ToggleVerificationMethodLink
            disabled={false}
            verificationMethod={verificationMethod}
            onSendEmail={onSendEmail}
            onSendTextMessage={onSendTextMessage}
          />
        )}
      </Stack>
    </Stack>
  );
}

function getMaskedPhone(phone?: string): string {
  if (!phone) return '';
  const lastFourDigits = phone.replace(/\D/g, '').slice(-4);
  if (!lastFourDigits) return '';
  return `••••${lastFourDigits}`;
}

interface ToggleVerificationMethodLinkProps {
  disabled: boolean;
  verificationMethod: SendVerificationCodeMethod;
  onSendEmail: () => void;
  onSendTextMessage: () => void;
}

function ToggleVerificationMethodLink({
  disabled,
  verificationMethod,
  onSendEmail,
  onSendTextMessage,
}: ToggleVerificationMethodLinkProps) {
  const isSMS = verificationMethod === 'sms';
  const label = isSMS
    ? 'Receive Code Via Email'
    : 'Receive Code Via Text Message';

  if (disabled) {
    return <Typography color="textSecondary">{label}</Typography>;
  }

  return (
    <Link
      color="primary"
      href="#"
      onClick={(event) => {
        event.preventDefault();
        if (isSMS) {
          onSendEmail();
        } else {
          onSendTextMessage();
        }
      }}
    >
      {label}
    </Link>
  );
}

interface ResendHelperProps {
  resendAvailableAt: DateTime | undefined;
  onResend: () => void;
}

function ResendHelper({ resendAvailableAt, onResend }: ResendHelperProps) {
  return (
    <Typography color="textSecondary">
      Didn&apos;t receive a code? Check your spam folder.
      <br />
      {resendAvailableAt ? (
        <Countdown targetDate={resendAvailableAt}>
          {(timeLeft, isElapsed) =>
            timeLeft && !isElapsed ? (
              <>Resend code in {timeLeft.toFormat('mm:ss')}</>
            ) : (
              <ResendCodeLink onResend={onResend} />
            )
          }
        </Countdown>
      ) : (
        <ResendCodeLink onResend={onResend} />
      )}
    </Typography>
  );
}

interface ResendCodeLinkProps {
  onResend: () => void;
}

function ResendCodeLink({ onResend }: ResendCodeLinkProps) {
  return (
    <Link
      color="primary"
      href="#"
      onClick={(event) => {
        event.preventDefault();
        onResend();
      }}
    >
      Resend code
    </Link>
  );
}
